import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { User, Lock, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import logo from './centrosura.png';
import FondoEspacial from './fondoEspacial/FondoEspacial';
import './LoginPanel.css';

const LoginView = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  // Al entrar, el fondo da el salto a velocidad warp antes de navegar
  const [despegando, setDespegando] = useState(false);
  const saltoRef = useRef(false);
  const saltoTimer = useRef(null);
  const [verClave, setVerClave] = useState(false);
  const [mayus, setMayus] = useState(false);
  const [sacudir, setSacudir] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();

  // Si llegó aquí desde una página protegida (ej. el link del correo de una
  // encuesta), al iniciar sesión vuelve a esa página en vez de a Inicio.
  const from = location.state?.from;
  const destino = from?.pathname && from.pathname !== '/login'
    ? `${from.pathname}${from.search || ''}`
    : '/inicio';

  useEffect(() => {
    if (user && !saltoRef.current) {
      navigate(destino, { replace: true });
    }
  }, [user, navigate, destino]);

  useEffect(() => () => clearTimeout(saltoTimer.current), []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!credentials.username.trim()) {
      newErrors.username = 'El usuario es requerido';
    }
    if (!credentials.password) {
      newErrors.password = 'La contraseña es requerida';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    saltoRef.current = true;
    try {
      await login(credentials.username, credentials.password);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        navigate(destino, { replace: true });
        return;
      }
      setDespegando(true);
      saltoTimer.current = setTimeout(() => navigate(destino, { replace: true }), 950);
    } catch (error) {
      saltoRef.current = false;
      setIsLoading(false);
      setErrors({
        general: error.message || 'Error al iniciar sesión'
      });
      setSacudir(true);
    }
  };

  // Aviso de Bloq Mayús mientras escribe la contraseña
  const revisarMayus = (e) => {
    if (e.getModifierState) setMayus(e.getModifierState('CapsLock'));
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-hidden bg-[#01040d]">
      <FondoEspacial modo={despegando ? 'salto' : isLoading ? 'cargando' : 'reposo'} />

      <div
        className="relative z-10 w-full max-w-[400px] px-4"
        style={{
          opacity: despegando ? 0 : 1,
          transform: despegando ? 'scale(1.06)' : 'none',
          transition: 'opacity 450ms ease-in, transform 600ms cubic-bezier(0.5, 0, 0.75, 0)',
        }}
      >
        <div className="lp-entrada">
          <div
            className={`lp-tarjeta px-7 pt-8 pb-6 sm:px-9 sm:pt-9 ${sacudir ? 'lp-sacudir' : ''}`}
            onAnimationEnd={() => setSacudir(false)}
          >
            <img
              src={logo}
              alt="Centro SURA"
              className="h-28 max-w-full object-contain mx-auto mb-8"
            />

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label htmlFor="login-usuario" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Usuario
                </label>
                <div className="relative lp-grupo">
                  <User size={18} className="lp-icono" />
                  <input
                    id="login-usuario"
                    type="text"
                    name="username"
                    placeholder="Ingresa tu usuario"
                    value={credentials.username}
                    onChange={handleChange}
                    disabled={isLoading}
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    autoFocus
                    aria-invalid={errors.username ? 'true' : 'false'}
                    aria-describedby={errors.username ? 'login-usuario-error' : undefined}
                    className="lp-campo"
                  />
                </div>
                {errors.username && (
                  <p id="login-usuario-error" className="mt-1.5 text-sm text-red-600">{errors.username}</p>
                )}
              </div>

              <div>
                <label htmlFor="login-clave" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Contraseña
                </label>
                <div className="relative lp-grupo">
                  <Lock size={18} className="lp-icono" />
                  <input
                    id="login-clave"
                    type={verClave ? 'text' : 'password'}
                    name="password"
                    placeholder="Ingresa tu contraseña"
                    value={credentials.password}
                    onChange={handleChange}
                    onKeyDown={revisarMayus}
                    onKeyUp={revisarMayus}
                    onBlur={() => setMayus(false)}
                    disabled={isLoading}
                    autoComplete="current-password"
                    aria-invalid={errors.password ? 'true' : 'false'}
                    aria-describedby={errors.password ? 'login-clave-error' : undefined}
                    className="lp-campo lp-campo-con-boton"
                  />
                  <button
                    type="button"
                    className="lp-ver-clave"
                    onClick={() => setVerClave((v) => !v)}
                    aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    aria-pressed={verClave}
                  >
                    {verClave ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {errors.password && (
                  <p id="login-clave-error" className="mt-1.5 text-sm text-red-600">{errors.password}</p>
                )}
                {mayus && !errors.password && (
                  <p className="mt-1.5 text-sm text-amber-600">Bloq Mayús está activado</p>
                )}
              </div>

              {errors.general && (
                <div role="alert" className="flex items-start gap-2.5 rounded-xl bg-red-50 border border-red-100 px-3.5 py-3">
                  <AlertCircle size={18} className="text-red-500 shrink-0 mt-px" />
                  <p className="text-sm text-red-700">{errors.general}</p>
                </div>
              )}

              <div className="pt-2">
                <button type="submit" disabled={isLoading} className="lp-boton">
                  {isLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Iniciando sesión</span>
                    </>
                  ) : (
                    <span>Iniciar sesión</span>
                  )}
                </button>
              </div>
            </form>

            <p className="mt-7 text-center text-xs text-slate-400">
              SURA de Venezuela
            </p>
          </div>

          <p className="mt-6 text-center text-sm text-white/55">
            Centro SURA 1.6.1
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginView;
